<template>
  <div class="profession-matching-game">
    <LevelComplete v-if="gameOver" />

    <div
      v-if="!gameOver"
      class="profession-targets"
    >
      <h2 class="profession-label">
        Who wears this?
      </h2>
      <h3>{{ currentUniform.name }}</h3>
      <div
        ref="targetRef"
        class="profession-target"
        @dragover.prevent
        @drop="handleDrop"
      >
        <img
          :src="currentUniform.image"
          alt="Uniform"
          class="uniform-image"
          @click="speakOutLoud(currentUniform.name)"
        >
      </div>
    </div>

    <div
      v-if="!gameOver"
      class="draggable-items"
    >
      <div
        v-for="person in professionals"
        :key="person.name"
        class="draggable-emoji"
        :style="draggedItem === person ? draggedStyle : {}"
        draggable="true"
        @dragstart="(e) => handleDragStart(e, person)"
        @click="speakOutLoud(person.name)"
        @touchstart="(e) => handleTouchStart(e, person)"
        @touchmove="handleTouchMove"
        @touchend="(e) => handleTouchEnd(e, targetRef)"
      >
        {{ person.emoji }}
      </div>
    </div>

    <div
      v-if="isTouchDragging && draggedItem"
      class="floating-emoji"
      :style="{ top: touchPosition.y + 'px', left: touchPosition.x + 'px' }"
    >
      {{ draggedItem.emoji }}
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import LevelComplete from "../LevelComplete.vue";
import { useDragAndDrop } from "@/composables/useDragAndDrop";
import { useSpeechSynthesis } from "@/composables/useSpeechSynthesis";

export default {
  components: { LevelComplete },
  setup() {
    const uniforms = [
      {
        name: "Doctor",
        image: "https://img.freepik.com/premium-vector/doctor-s-coat-icon_854353-189.jpg",
      },
      {
        name: "Chef",
        image: "https://static.vecteezy.com/system/resources/previews/019/516/657/non_2x/line-art-chef-uniform-mockup-vector.jpg",
      },
      {
        name: "Engineer",
        image: "https://img.freepik.com/free-vector/work-wear-uniform-set_1284-68407.jpg?semt=ais_hybrid&w=740",
      },
      {
        name: "Artist",
        image: "https://img.freepik.com/free-vector/artist-concept-illustration_114360-6775.jpg",
      },
      {
        name: "Teacher",
        image: "https://img.freepik.com/premium-vector/female-teacher-cute-woman-stands-with-pointer-book-school-learning-concept-teacher-s-day_335402-428.jpg?semt=ais_hybrid&w=740",
      },
    ];

    const professionals = [
      { name: "Doctor", emoji: "👩‍⚕️" },
      { name: "Chef", emoji: "👨‍🍳" },
      { name: "Engineer", emoji: "👷" },
      { name: "Artist", emoji: "👨‍🎨" },
      { name: "Teacher", emoji: "👩‍🏫" },
    ];

    const currentIndex = ref(0);
    const gameOver = ref(false);
    const draggedItem = ref(null);
    const draggedStyle = ref({});
    const targetRef = ref(null);

    const isTouchDragging = ref(false);
    const touchPosition = ref({ x: 0, y: 0 });

    const { speakOutLoud } = useSpeechSynthesis();

    const currentUniform = computed(() =>
      gameOver.value ? null : uniforms[currentIndex.value]
    );

    const playSound = (url) => new Audio(url).play();

    const {
      handleDragStart,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      handleDrop: internalHandleDrop,
    } = useDragAndDrop({
      onDragStart: (item) => {
        draggedItem.value = item;
      },
      onDragEnd: () => {
        draggedItem.value = null;
        draggedStyle.value = {};
        isTouchDragging.value = false;
      },
      onDrop: ({ data, valid }) => {
        if (!valid || !currentUniform.value) return;

        speakOutLoud(data.name);
        playSound('https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a');

        setTimeout(() => {
          currentIndex.value++;
          if (currentIndex.value >= uniforms.length) {
            gameOver.value = true;
            speakOutLoud("Well done!");
            setTimeout(() => {
              window.location.href = "/professions";
            }, 4000);
          }
        }, 1500);
      },
      isValidDropTarget: (data) =>
        currentUniform.value && data.name === currentUniform.value.name,
    });

    const handleDrop = (event) => {
      if (!currentUniform.value) return;
      try {
        const data = JSON.parse(event.dataTransfer.getData("text/plain"));
        const valid = data.name === currentUniform.value.name;

        if (valid) {
          internalHandleDrop(event, targetRef.value);
        } else {
          speakOutLoud("Try again");
        }
      } catch (err) {
        console.error("Invalid drag data", err);
      }
    };

    const handleTouchStartOverride = (e, item) => {
      isTouchDragging.value = true;
      touchPosition.value = {
        x: e.touches[0].clientX - 25,
        y: e.touches[0].clientY - 25,
      };
      handleTouchStart(e, item);
    };

    const handleTouchMoveOverride = (e) => {
      if (!isTouchDragging.value) return;
      touchPosition.value = {
        x: e.touches[0].clientX - 25,
        y: e.touches[0].clientY - 25,
      };
      handleTouchMove(e);
    };

    const handleTouchEndOverride = (e, targetRef) => {
      isTouchDragging.value = false;
      handleTouchEnd(e, targetRef);
    };

    onMounted(() => {
      if (currentUniform.value) {
        speakOutLoud("Who wears this?");
      }
    });

    watch(currentUniform, (newVal, oldVal) => {
      if (newVal && newVal.name !== oldVal?.name) {
        speakOutLoud("Who wears this?");
      }
    });

    return {
      currentUniform,
      professionals,
      handleDragStart,
      handleTouchStart: handleTouchStartOverride,
      handleTouchMove: handleTouchMoveOverride,
      handleTouchEnd: handleTouchEndOverride,
      handleDrop,
      draggedItem,
      draggedStyle,
      targetRef,
      speakOutLoud,
      gameOver,
      isTouchDragging,
      touchPosition,
    };
  },
};
</script>

<style scoped>
.profession-matching-game {
  position: relative;
  max-width: 500px;
  width: 80%;
  margin: auto;
}

.profession-target {
  position: relative;
  height: 300px;
  background-color: #eee;
  border-radius: 10px;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.uniform-image {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  border-radius: 10px;
}

.profession-label {
  margin: 10px auto;
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  max-width: 200px;
  text-align: center;
}

.draggable-emoji {
  font-size: 5rem;
  display: inline-block;
  margin: 10px;
  cursor: move;
}

.floating-emoji {
  position: fixed;
  font-size: 5rem;
  pointer-events: none;
  z-index: 9999;
  transform: translate(-50%, -50%);
}
</style>
